import { useQuery } from '@tanstack/react-query';

import {
  safetyStockService,
  type SafetyStockRunListItem,
} from '@/services/safetyStockService';
import { safetyStockRunsQueryKey } from './useSafetyStockConfig';

const STALE_MS = 60 * 1000;
const GC_MS = 10 * 60 * 1000;

export function useSafetyStockRunsList(
  warehouseId: number | null,
  pageSize = 10,
  page = 1,
) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'list', warehouseId, page, pageSize],
    enabled: typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.listRuns(
        { page, page_size: pageSize, warehouse_id: warehouseId as number },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    refetchInterval: (query) => {
      const rows = query.state.data?.results || [];
      return rows.some((r) => r.status === 'RUNNING') ? 2000 : false;
    },
    placeholderData: (prev) => prev,
  });
}

/** Último run de la lista (RUNNING primero si existe) — para la barra de estado. */
export function pickLatestRun(
  runs: SafetyStockRunListItem[] | undefined,
): SafetyStockRunListItem | null {
  if (!runs?.length) return null;
  return runs.find((r) => r.status === 'RUNNING') || runs[0] || null;
}

/**
 * Último run con resumen analítico usable (KPIs / matriz / desglose).
 * Nunca elige RUNNING: durante un recálculo se sigue mostrando el anterior.
 */
export function pickLatestAnalyzableRun(
  runs: SafetyStockRunListItem[] | undefined,
): SafetyStockRunListItem | null {
  if (!runs?.length) return null;
  return (
    runs.find(
      (r) =>
        r.summary_ready &&
        (r.status === 'SUCCESS' || r.status === 'PARTIAL_SUCCESS'),
    ) ||
    runs.find((r) => r.status === 'SUCCESS' || r.status === 'PARTIAL_SUCCESS') ||
    null
  );
}

export function useSafetyStockRunSummary(
  runId: string | null | undefined,
  warehouseId: number | null,
) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'summary', runId, warehouseId],
    enabled: Boolean(runId) && typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.getRunSummary(
        runId as string,
        { warehouse_id: warehouseId as number },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useSafetyStockCurrentSummary(
  warehouseId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'current-policy', warehouseId],
    enabled: enabled && typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.getCurrentSummary(
        { warehouse_id: warehouseId as number },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}

export function useSafetyStockBreakdown(
  runId: string | null | undefined,
  dimension: string,
  warehouseId: number | null,
) {
  return useQuery({
    queryKey: [
      ...safetyStockRunsQueryKey,
      'breakdown',
      runId,
      dimension,
      warehouseId,
    ],
    enabled: Boolean(runId) && Boolean(dimension) && typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.getRunBreakdown(
        runId as string,
        dimension,
        { warehouse_id: warehouseId as number },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useSafetyStockRunDetail(runId: string | null | undefined, poll = false) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'detail', runId],
    enabled: Boolean(runId),
    queryFn: ({ signal }) => safetyStockService.getRun(runId as string, { signal }),
    staleTime: poll ? 0 : STALE_MS,
    gcTime: GC_MS,
    refetchInterval: poll ? 2000 : false,
  });
}

export function useSafetyStockRunErrors(
  runId: string | null | undefined,
  warehouseId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...safetyStockRunsQueryKey, 'errors', runId, warehouseId],
    enabled: Boolean(runId) && enabled && typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.getRunErrors(
        runId as string,
        { page: 1, page_size: 5, warehouse_id: warehouseId as number },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}

export function useSafetyStockCompareRuns(
  runA: string | null | undefined,
  runB: string | null | undefined,
  dimension: string,
  warehouseId: number | null,
) {
  return useQuery({
    queryKey: [
      ...safetyStockRunsQueryKey,
      'compare',
      runA,
      runB,
      dimension,
      warehouseId,
    ],
    enabled:
      Boolean(runA) &&
      Boolean(runB) &&
      runA !== runB &&
      Boolean(dimension) &&
      typeof warehouseId === 'number',
    queryFn: ({ signal }) =>
      safetyStockService.compareRuns(
        {
          run_a: runA as string,
          run_b: runB as string,
          dimension,
          warehouse_id: warehouseId as number,
        },
        { signal },
      ),
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}
